import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
    ArrowLeft,
    Sparkles,
    Star,
    ShoppingBag,
    Check,
    Zap,
    Info,
} from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function MarketplaceAsset() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [data, setData] = useState(null);
    const [buying, setBuying] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");

    // Phase VI.5 Wave A · Hotfix R1 · Issue 2 ·
    // Defensive guard against routers that mis-rank `/marketplace/library`
    // against `/marketplace/:slug`. If the dynamic route is reached with
    // the reserved slug "library" we transparently redirect to the real
    // library page · Zero-Touch (additive · legacy slugs untouched).
    const RESERVED_SLUGS = { library: "/marketplace/library" };
    const _reservedTarget = slug && RESERVED_SLUGS[slug];

    const load = useCallback(async () => {
        try {
            const d = await api.marketplaceAsset(slug);
            setData(d);
        } catch (e) {
            toast.error(e.message || "Failed to load");
        }
    }, [slug]);

    useEffect(() => {
        load();
    }, [load]);

    // Phase VI.5 Wave A · Hotfix R1 · Issue 2 · reserved-slug redirect
    // (must come AFTER all hooks to respect rules-of-hooks).
    if (_reservedTarget) {
        return <Navigate to={_reservedTarget} replace />;
    }

    async function buy() {
        if (!data) return;
        setBuying(true);
        try {
            const res = await api.marketplaceBuy(data.asset.id);
            toast.success(`Bought! ${res.credits_spent} credits spent · balance ${res.balance}`);
            await refresh();
            await load();
            setShowConfirm(false);
        } catch (e) {
            if (e.data?.detail?.error === "already_owned") {
                toast.info("Already in your library");
                navigate("/marketplace/library");
            } else {
                toast.error(e.message || "Purchase failed");
            }
        } finally {
            setBuying(false);
        }
    }

    async function submitReview() {
        try {
            await api.marketplaceReviewCreate(data.asset.id, rating, comment || null);
            toast.success("Review submitted!");
            setComment("");
            await load();
        } catch (e) {
            toast.error(e.message || "Review failed");
        }
    }

    if (!data) {
        return (
            <SidebarLayout>
                <div className="text-text-secondary text-sm">Loading asset…</div>
            </SidebarLayout>
        );
    }

    const a = data.asset;
    return (
        <SidebarLayout>
            <div className="mx-auto max-w-6xl">
                <Link
                    to="/marketplace/v10"
                    className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-main mb-4"
                    data-testid="asset-back"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to marketplace
                </Link>

                <div className="grid gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2 space-y-6">
                        <motion.div
                            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                        >
                            <div className="flex items-start gap-4">
                                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[var(--color-primary)]/30 to-[var(--color-secondary)]/30 grid place-items-center">
                                    <ShoppingBag className="h-7 w-7" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                        {a.asset_type.replace(/_/g, " ")}
                                    </p>
                                    <h1 data-testid="asset-title" className="text-2xl font-semibold mt-0.5">
                                        {a.name}
                                    </h1>
                                    <p className="text-text-secondary text-sm mt-2">{a.description}</p>
                                </div>
                            </div>
                        </motion.div>

                        {a.long_description && (
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                                <p className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                                    About this asset
                                </p>
                                <div className="prose prose-invert prose-sm max-w-none">
                                    <ReactMarkdown>{a.long_description}</ReactMarkdown>
                                </div>
                            </div>
                        )}

                        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                            <p className="text-xs uppercase tracking-widest text-text-secondary mb-3">
                                What's included
                            </p>
                            <IncludedPreview asset={a} />
                        </div>

                        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                            <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-text-secondary mb-3">
                                <Star className="h-3 w-3" /> Reviews
                                {a.stats?.rating_count > 0 && (
                                    <span className="text-text-main ml-2">
                                        {a.stats.rating_avg.toFixed(1)} · {a.stats.rating_count}
                                    </span>
                                )}
                            </p>
                            {data.reviews.length === 0 ? (
                                <p className="text-text-secondary text-sm">No reviews yet.</p>
                            ) : (
                                <div className="space-y-3">
                                    {data.reviews.map((r) => (
                                        <div key={r.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3">
                                            <div className="flex items-center justify-between text-xs text-text-secondary">
                                                <span>{r.buyer_name}</span>
                                                <span className="flex items-center gap-0.5 text-[var(--color-primary)]">
                                                    {Array.from({ length: r.rating }).map((_, i) => (
                                                        <Star key={i} className="h-3 w-3 fill-current" />
                                                    ))}
                                                </span>
                                            </div>
                                            {r.comment && <p className="text-sm mt-1">{r.comment}</p>}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {data.owned && (
                                <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3">
                                    <p className="text-xs text-text-secondary mb-2">Leave a review</p>
                                    <div className="flex items-center gap-1 mb-2">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <button
                                                key={n}
                                                onClick={() => setRating(n)}
                                                data-testid={`review-star-${n}`}
                                                className={rating >= n ? "text-[var(--color-primary)]" : "text-text-secondary/40"}
                                            >
                                                <Star className={`h-5 w-5 ${rating >= n ? "fill-current" : ""}`} />
                                            </button>
                                        ))}
                                    </div>
                                    <textarea
                                        rows={2}
                                        placeholder="Optional comment…"
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-sm outline-none"
                                        data-testid="review-comment"
                                    />
                                    <button
                                        onClick={submitReview}
                                        data-testid="review-submit"
                                        className="mt-2 rounded-full bg-[var(--color-primary)]/20 text-[var(--color-primary)] px-3 py-1 text-xs"
                                    >
                                        Submit review
                                    </button>
                                </div>
                            )}
                        </div>

                        {data.similar.length > 0 && (
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                                <p className="text-xs uppercase tracking-widest text-text-secondary mb-3">
                                    Similar assets
                                </p>
                                <div className="grid gap-3 md:grid-cols-3">
                                    {data.similar.map((s) => (
                                        <Link
                                            key={s.id}
                                            to={`/marketplace/${s.slug}`}
                                            className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3 hover:border-[var(--color-primary)]/50"
                                        >
                                            <p className="text-sm font-medium truncate">{s.name}</p>
                                            <p className="text-xs text-text-secondary mt-1">
                                                {s.price_credits_base} credits
                                            </p>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <aside className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 h-fit sticky top-6">
                        <p className="text-xs uppercase tracking-widest text-text-secondary">Price</p>
                        <p className="text-4xl font-semibold mt-1" data-testid="asset-price">
                            {data.pricing.effective}
                            <span className="text-sm text-text-secondary ml-1">credits</span>
                        </p>
                        {data.pricing.multiplier !== 1 && (
                            <p className="text-xs text-text-secondary mt-1">
                                {data.pricing.base} base × {data.pricing.multiplier} surge
                            </p>
                        )}

                        {data.owned ? (
                            <div className="mt-5 space-y-2">
                                <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-success)]/10 border border-[var(--color-success)]/40 px-3 py-1.5 text-xs text-[var(--color-success)]">
                                    <Check className="h-3.5 w-3.5" /> Owned
                                </div>
                                <Link
                                    to="/marketplace/library"
                                    data-testid="asset-view-library"
                                    className="mt-3 block w-full rounded-full bg-[var(--color-primary)]/20 text-[var(--color-primary)] py-2.5 text-sm text-center font-medium"
                                >
                                    View in Library →
                                </Link>
                            </div>
                        ) : !data.can_buy ? (
                            <div className="mt-5 space-y-2">
                                <div className="rounded-lg border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 p-3 text-xs text-[var(--color-alert)]">
                                    <Info className="h-3.5 w-3.5 inline mr-1" />
                                    Requires plan <b>{a.required_min_plan}</b>
                                </div>
                                <Link
                                    to="/app/subscription"
                                    className="mt-2 block w-full rounded-full bg-white hover:bg-[#EDEDED] py-2.5 text-sm text-center font-medium text-black"
                                >
                                    Upgrade plan
                                </Link>
                            </div>
                        ) : showConfirm ? (
                            <div className="mt-5 space-y-2 rounded-lg border border-[var(--color-primary)]/40 bg-[var(--color-primary)]/10 p-3">
                                <p className="text-sm">
                                    Confirm buying <b>{a.name}</b> for <b>{data.pricing.effective}</b> credits?
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        data-testid="asset-buy-confirm"
                                        onClick={buy}
                                        disabled={buying}
                                        className="flex-1 rounded-full bg-white hover:bg-[#EDEDED] py-2 text-sm font-medium text-black disabled:opacity-50"
                                    >
                                        {buying ? "Buying…" : "Confirm"}
                                    </button>
                                    <button
                                        onClick={() => setShowConfirm(false)}
                                        className="flex-1 rounded-full border border-[var(--color-border)] py-2 text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                data-testid="asset-buy-btn"
                                onClick={() => setShowConfirm(true)}
                                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#EDEDED] py-2.5 text-sm font-medium text-black"
                            >
                                <Zap className="h-4 w-4" />
                                Buy for {data.pricing.effective} credits
                            </button>
                        )}
                        <p className="mt-3 text-[11px] text-text-secondary leading-relaxed">
                            <b>Personal use license.</b> Applied immediately to your workspace after purchase.
                        </p>
                    </aside>
                </div>
            </div>
        </SidebarLayout>
    );
}

function IncludedPreview({ asset }) {
    const c = asset.content || {};
    if (asset.asset_type === "outreach_template_pack") {
        const tpls = c.templates || [];
        return (
            <ul className="space-y-2 text-sm">
                {tpls.map((t, i) => (
                    <li key={i} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2">
                        <p className="font-medium">{t.name}</p>
                        <p className="text-xs text-text-secondary mt-0.5 truncate">{t.subject}</p>
                    </li>
                ))}
            </ul>
        );
    }
    if (asset.asset_type === "outreach_sequence") {
        const steps = c.steps || [];
        return (
            <ol className="space-y-2 text-sm">
                {steps.map((s, i) => (
                    <li key={i} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2">
                        <p className="text-xs text-text-secondary">Step {s.step_number} · +{s.delay_days} days</p>
                        <p className="mt-1">{s.subject}</p>
                    </li>
                ))}
            </ol>
        );
    }
    if (asset.asset_type === "lead_list_pack") {
        const leads = c.leads || [];
        return (
            <p className="text-sm text-text-secondary">
                <b>{leads.length}</b> hand-curated leads — full contact + LinkedIn URLs.
            </p>
        );
    }
    if (asset.asset_type === "agent_config") {
        return (
            <pre className="text-xs bg-[var(--color-panel-ai)] rounded p-3 overflow-x-auto">{JSON.stringify(c, null, 2)}</pre>
        );
    }
    if (asset.asset_type === "badge_cosmetic") {
        return (
            <p className="text-sm text-text-secondary">
                Decorative <b>{c.tier || "gold"}</b>-tier badge, added permanently to your public profile.
            </p>
        );
    }
    if (asset.asset_type === "custom_module_ref") {
        return (
            <p className="text-sm text-text-secondary">
                Grants permanent access to a Pro-tier custom module — bypasses the plan gate for you.
            </p>
        );
    }
    return <pre className="text-xs bg-[var(--color-panel-ai)] rounded p-3 overflow-x-auto">{JSON.stringify(c, null, 2)}</pre>;
}
